import api from './api';

export interface ClientContact {
  id: number;
  last_name: string;
  first_name: string;
  patronymic: string | null;
  position: string | null;
  phone: string | null;
  email: string | null;
  birth_date: string | null;
  note: string | null;
}

// Тип для контактов при создании/обновлении (id может быть undefined для новых контактов)
export interface ClientContactInput {
  id?: number;
  first_name: string;
  last_name: string;
  patronymic: string | null;
  position: string | null;
  phone: string | null;
  email: string | null;
  birth_date: string | null;
  note: string | null;
}

// Тип для данных создания/обновления клиента
export type ClientInput = Omit<Partial<Client>, 'contacts'> & {
  client_type: 'individual' | 'legal';
  contacts?: ClientContactInput[];
};

export interface Client {
  id: number;
  name: string;
  tag?: string | null;
  client_type?: 'individual' | 'legal';
  inn?: string | null;
  legal_name?: string | null;
  legal_address?: string | null;
  curator: string | null;
  curator_id: number | null;
  field_of_activity: string | null;
  projects: number;
  cases: number;
  tasks: number;
  cooperation_year: number | null;
  note?: string | null;
  contacts?: ClientContact[];
}

export interface ClientsResponse {
  data: Client[];
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface ClientsStatistics {
  total_clients: number;
  clients_this_month: number;
}

export interface FilterOptions {
  client_names: string[];
  curators: string[];
  fields_of_activity: string[];
  years: number[];
}

export interface ClientsFilters {
  client?: string;
  curator?: string;
  field_of_activity?: string;
  without_tasks?: boolean;
  year?: string;
  page?: number;
  per_page?: number;
}

export const clientsService = {
  /**
   * Получить список клиентов с фильтрацией и пагинацией
   */
  async getClients(filters: ClientsFilters = {}): Promise<ClientsResponse> {
    const params = new URLSearchParams();
    
    if (filters.client) params.append('client', filters.client);
    if (filters.curator) params.append('curator', filters.curator);
    if (filters.field_of_activity) params.append('field_of_activity', filters.field_of_activity);
    if (filters.without_tasks) params.append('without_tasks', '1');
    if (filters.year) params.append('year', filters.year);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());

    const { data } = await api.get<ClientsResponse>(`/clients?${params.toString()}`);
    return data;
  },

  /**
   * Получить статистику по клиентам
   */
  async getStatistics(): Promise<ClientsStatistics> {
    const { data } = await api.get<ClientsStatistics>('/clients/statistics');
    return data;
  },

  /**
   * Получить опции для фильтров
   */
  async getFilterOptions(): Promise<FilterOptions> {
    const { data } = await api.get<FilterOptions>('/clients/filter-options');
    return data;
  },

  /**
   * Получить клиента по ID
   */
  async getClient(id: number): Promise<Client> {
    const { data } = await api.get<Client>(`/clients/${id}`);
    return data;
  },

  /**
   * Создать нового клиента
   */
  async createClient(clientData: ClientInput): Promise<Client> {
    const { data } = await api.post<{ data: Client }>('/clients', clientData);
    return data.data;
  },

  /**
   * Обновить клиента
   */
  async updateClient(id: number, clientData: ClientInput): Promise<Client> {
    const { data } = await api.put<{ data: Client }>(`/clients/${id}`, clientData);
    return data.data;
  },

  /**
   * Удалить клиента
   */
  async deleteClient(id: number): Promise<void> {
    await api.delete(`/clients/${id}`);
  },
};
